import React from 'react'
import { Outlet, type RouteObject, useLoaderData, Navigate } from 'react-router'
import { useEffect } from 'react'
import { Workspace } from '@/features/iam/workspaces/Workspace'
import type { IamFeature } from '@/features/iam'
import type { AuthStore } from '@/features/iam/auth/AuthStore'
import { observer } from 'mobx-react-lite'
import type Session from '@/features/iam/auth/Session'
import { WorkspaceMember } from '@/features/iam/members/WorkspaceMember'

const WorkspaceSelectorView = React.lazy(
  () => import('@/features/iam/workspaces/WorkspaceSelectorView'),
)
const WorkspaceSignupView = React.lazy(
  () =>
    import('@/features/iam/workspaces/WorkspaceSignupView/WorkspaceSignupViewContainer'),
)

/**
 * Wraps the specified routes in a pathless route which requires authentication.
 *
 * @param iam
 * @param routes
 */
export function authenticated(
  iam: IamFeature,
  routes: RouteObject[],
): RouteObject {
  const { authStore, session } = iam

  return {
    children: routes,
    loader: ({ request }) =>
      authStore
        .checkAuthentication(request.signal)
        .then(async (isAuthenticated) => {
          // If authenticated, check if we have a pre-selected workspace.
          if (isAuthenticated) {
            await session.preselectWorkspace(request.signal)
            return null
          }

          await iam.authStore.onUnauthenticated()

          const returnUrl = location.pathname + location.search

          // If we'd be returning to the root or logout, don't bother adding the query string.
          if (returnUrl === '/' || returnUrl === '/logout') {
            return '/login'
          }

          // Serialize a query string and redirect to log in.
          const qs = new URLSearchParams({
            returnUrl,
          })
          return `/login?${qs.toString()}`
        }),
    // When token renewal fails, we'll refresh the page.
    Component: () => {
      const redirectTarget = useLoaderData<string | null>()

      if (redirectTarget) {
        return <Navigate to={redirectTarget} />
      }

      return <Authenticated authStore={authStore} session={session} />
    },
  }
}

/**
 * Handles page reload when token expires and ensures that a workspace
 * is selected before we render the main app.
 */
const Authenticated = observer(function Authenticated({
  session,
  authStore,
}: {
  session: Session
  authStore: AuthStore
}) {
  useEffect(() => {
    const subscription = authStore.tokenRenewalFailed$.subscribe((err) => {
      // Reload the page when token renewal fails, which will trigger
      // a redirect to authenticate.
      console.warn('Token renewal failed', err)
      window.location.reload()
    })

    return () => subscription.unsubscribe()
  }, [authStore])

  if (session.currentWorkspace === Workspace.none) {
    // We need a workspace before we can render the main app.
    return <WorkspaceSelectorView />
  }

  if (
    session.currentMember === WorkspaceMember.none ||
    !session.currentMember.isProfileComplete
  ) {
    // A workspace is selected, but we're not a member.
    return <WorkspaceSignupView />
  }

  return <Outlet />
})
